import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import {
  TripAdvisorInfo,
  TripAdvisorRating,
  TripAdvisorReviewComponent,
} from '@hotelplan/components.common.tripadvisor-rating';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import PdpPageContainer from 'components/domain/pdp/PdpPageContainer';
import { useGetPdpRatingQuery } from 'graphql/pdp/GetPdpRating.generated';
import PdpRatingSkeleton from './PdpRatingSkeleton.skeleton';

const Title = styled.h2(
  sx2CssThemeFn({
    color: 'secondary',
    borderBottom: '1px solid',
    borderColor: '#e4e4e4',
    mb: 3,
    pb: 1,
    fontSize: ['18px', '26px'],
  })
);

const RatingInfoWrapper = styled.div(
  sx2CssThemeFn({
    display: 'grid',
    gridTemplateColumns: [null, '400px 400px'],
    gap: [3, '70px'],
    mb: [2, '60px'],
    '.rating-wrap': {
      div: {
        fontSize: ['14px', 0],
      },
    },
    '.tripAdvisorInfoText': {
      fontSize: ['14px', 0],
    },
  })
);

const TAInfo = styled(TripAdvisorInfo)(
  sx2CssThemeFn({
    display: 'block',
    '.-rating': {
      mb: [2, 1],
      '.icon': {
        mr: '2px',
      },
    },
  })
);
const TASubRating = styled(TripAdvisorRating)(
  sx2CssThemeFn({
    fontSize: 1,
    mb: 2,
    '.icon': {
      mr: '2px',
    },
  })
);

const TAReview = styled(TripAdvisorReviewComponent)(
  sx2CssThemeFn({
    fontSize: '14px',
    lineHeight: '1.5',
    borderBottom: '1px solid #e4e4e4',
    mb: [2, 3],
    pb: [2, 3],
    '.review-header': {
      display: ['grid', 'block'],
      gridTemplateColumns: ['1fr 1fr', '1fr'],
      alignItems: 'start',
      width: [null, 'auto'],
      flex: [null, '0 0 195px'],
    },
    '.review-comment': {
      width: 'auto',
      flex: '1 0',
      div: {
        fontSize: 0,
        letterSpacing: 'initial',
      },
    },
    '.review-description': {
      pt: [0, 2],
      display: 'flex',
      flexWrap: 'wrap',
      fontSize: ['11px', '13px'],
      color: 'textGray',
      '.review-date': {
        order: '-1',
        color: 'darkGrey',
        fontWeight: 'bold',
        width: '100%',
      },
      '.review-region:after': {
        fontSize: '0px',
      },
    },

    button: {
      color: 'primary',
      fontSize: '14px',
    },
    '.icon': {
      mr: '2px',
      height: '17px',
      width: '17px',
    },
    h3: {
      fontSize: '14px',
      fontFamily: 'Arial',
      fontWeight: 'bold',
      mb: [2, 2],
    },
  })
);

const PdpRating: React.FC = () => {
  const [t] = useTranslation();
  const { id } = useObjectId();
  const { loading, data } = useGetPdpRatingQuery({ variables: { id } });

  if (loading) {
    return (
      <PdpPageContainer>
        <PdpRatingSkeleton />
      </PdpPageContainer>
    );
  }

  const { taRating, subratings, reviews } = data?.pdp.content.tripAdvisor;

  return (
    <PdpPageContainer>
      <RatingInfoWrapper>
        <div>
          <Title>{t('pdp:tripAdvisor.averageRating')}</Title>
          <TAInfo
            rating={taRating?.rating}
            reviewCount={taRating?.count}
            label={t('common:tripAdvisor.reviewsCount')}
          />
        </div>
        <div>
          <Title>{t('pdp:tripAdvisor.detailedRating')}</Title>
          <div className="rating-wrap">
            {subratings &&
              subratings.map(
                ({ rating: subRatingValue, name: subRatingName }, i) => (
                  <TASubRating
                    key={i}
                    rating={subRatingValue}
                    label={subRatingName}
                    size="small"
                  />
                )
              )}
          </div>
        </div>
      </RatingInfoWrapper>
      <div>
        <Title>{t('pdp:tripAdvisor.currentReviews')}</Title>
        {(reviews || []).map((review, i) => {
          return <TAReview key={i} {...review} />;
        })}
      </div>
    </PdpPageContainer>
  );
};

export default PdpRating;
